import {
  attractionCategoryCrudConfig,
  type AttractionCategory,
} from '@/config/objects/attraction-categories';
import { CrudListView, type CrudConfig } from '@startupquickstart/frontend';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface AttractionCategoriesListProps {
  /**
   * Optional custom config (with modified endpoints, actions, etc.)
   */
  config?: CrudConfig<AttractionCategory>;
  /**
   * Optional default where clause for filtering
   */
  defaultWhere?: Record<string, unknown>;
  /**
   * Optional custom onItemClick handler
   */
  onItemClick?: (category: AttractionCategory) => void;
  /**
   * Custom class name for the container
   */
  className?: string;
  /**
   * Base URL for navigation, defaults to /attraction-categories
   */
  baseUrl?: string;
}

/**
 * Reusable attraction categories list component
 * Pass a custom config with modified actions to customize behavior
 */
export function AttractionCategoriesList({
  config,
  baseUrl = '/attraction-categories',
  defaultWhere,
  onItemClick,
  className,
}: AttractionCategoriesListProps) {
  const navigate = useNavigate();

  const _onItemClick = useCallback(
    (category: AttractionCategory) => {
      if (onItemClick) {
        onItemClick(category);
      } else {
        navigate(`/attraction-categories/${category.id}`);
      }
    },
    [navigate, onItemClick],
  );

  return (
    <div className={className}>
      <CrudListView
        config={config || attractionCategoryCrudConfig}
        baseUrl={baseUrl}
        onItemClick={_onItemClick}
        defaultWhere={defaultWhere}
        getValueFromItem={(category: AttractionCategory) => category.id}
        getLabelFromItem={(category: AttractionCategory) => category.name}
      />
    </div>
  );
}
